/**
 * CircuitBreaker - Proteção contra falhas em cascata na API
 *
 * Implementa o padrão Circuit Breaker para proteger o sistema quando
 * a API está instável ou indisponível.
 *
 * Estados:
 * - CLOSED: Funcionando normalmente, requisições passam
 * - OPEN: API falhando, requisições são bloqueadas
 * - HALF_OPEN: Testando se API voltou, permite uma requisição de teste
 *
 * Transições:
 * - CLOSED → OPEN: Após failureThreshold falhas consecutivas
 * - OPEN → HALF_OPEN: Após resetTimeout milissegundos
 * - HALF_OPEN → CLOSED: Se requisição de teste suceder
 * - HALF_OPEN → OPEN: Se requisição de teste falhar
 */

import { recordError, recordEvent } from '../observability/events'
import { syncLogger } from './SyncLogger'

/**
 * Estados do Circuit Breaker
 */
export const CircuitState = {
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
  HALF_OPEN: 'HALF_OPEN',
}

/**
 * Configuração padrão do Circuit Breaker
 */
const DEFAULT_CONFIG = {
  // Número de falhas para abrir o circuito
  failureThreshold: 5,

  // Tempo (ms) para tentar resetar após abrir (30 segundos)
  resetTimeout: 30000,

  // Tempo (ms) para considerar uma requisição como timeout (5 minutos)
  requestTimeout: 300000,

  // Número de sucessos necessários para fechar o circuito em HALF_OPEN
  successThreshold: 2,

  // Tempo mínimo entre tentativas em HALF_OPEN (5 segundos)
  halfOpenDelay: 5000,
}

/**
 * Classe Circuit Breaker
 */
class CircuitBreaker {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }

    // Estado por endpoint (permite circuitos separados)
    this.circuits = new Map()

    // Estado global (fallback)
    this.globalCircuit = this._createCircuitState()

    // Carregar estado persistido
    this._loadState()
  }

  /**
   * Cria estado inicial de um circuito
   */
  _createCircuitState() {
    return {
      state: CircuitState.CLOSED,
      failures: 0,
      successes: 0,
      lastFailureTime: null,
      lastSuccessTime: null,
      lastStateChange: Date.now(),
      totalFailures: 0,
      totalSuccesses: 0,
      consecutiveFailures: 0,
    }
  }

  /**
   * Obtém ou cria circuito para um endpoint
   */
  _getCircuit(endpoint = 'global') {
    if (endpoint === 'global') {
      return this.globalCircuit
    }

    if (!this.circuits.has(endpoint)) {
      this.circuits.set(endpoint, this._createCircuitState())
    }

    return this.circuits.get(endpoint)
  }

  /**
   * Verifica se requisição pode prosseguir
   *
   * @param {string} endpoint - Identificador do endpoint (opcional)
   * @returns {Object} { allowed: boolean, reason: string }
   */
  canExecute(endpoint = 'global') {
    const circuit = this._getCircuit(endpoint)

    switch (circuit.state) {
      case CircuitState.CLOSED:
        return { allowed: true, reason: 'circuit_closed' }

      case CircuitState.OPEN:
        // Verificar se já passou o tempo de reset
        const timeSinceOpen = Date.now() - circuit.lastStateChange

        if (timeSinceOpen >= this.config.resetTimeout) {
          // Transição para HALF_OPEN
          this._transitionTo(circuit, CircuitState.HALF_OPEN, endpoint)
          return { allowed: true, reason: 'testing_recovery' }
        }

        return {
          allowed: false,
          reason: 'circuit_open',
          retryAfter: this.config.resetTimeout - timeSinceOpen,
        }

      case CircuitState.HALF_OPEN:
        // Em HALF_OPEN, permitir apenas após delay
        const timeSinceLastAttempt =
          Date.now() - (circuit.lastFailureTime || circuit.lastStateChange)

        if (timeSinceLastAttempt >= this.config.halfOpenDelay) {
          return { allowed: true, reason: 'half_open_test' }
        }

        return {
          allowed: false,
          reason: 'half_open_waiting',
          retryAfter: this.config.halfOpenDelay - timeSinceLastAttempt,
        }

      default:
        return { allowed: true, reason: 'unknown_state' }
    }
  }

  /**
   * Registra sucesso de uma requisição
   *
   * @param {string} endpoint - Identificador do endpoint
   */
  recordSuccess(endpoint = 'global') {
    const circuit = this._getCircuit(endpoint)

    circuit.successes++
    circuit.totalSuccesses++
    circuit.consecutiveFailures = 0
    circuit.lastSuccessTime = Date.now()

    if (circuit.state === CircuitState.HALF_OPEN) {
      // Verificar se atingiu threshold de sucesso
      if (circuit.successes >= this.config.successThreshold) {
        this._transitionTo(circuit, CircuitState.CLOSED, endpoint)
      }
    } else if (circuit.state === CircuitState.OPEN) {
      // Não deveria acontecer, mas resetar se sucesso em OPEN
      this._transitionTo(circuit, CircuitState.CLOSED, endpoint)
    }

    this._saveState()
  }

  /**
   * Registra falha de uma requisição
   *
   * @param {string} endpoint - Identificador do endpoint
   * @param {Error} error - Erro que ocorreu
   */
  recordFailure(endpoint = 'global', error = null) {
    const circuit = this._getCircuit(endpoint)

    circuit.failures++
    circuit.totalFailures++
    circuit.consecutiveFailures++
    circuit.lastFailureTime = Date.now()
    circuit.lastError = error?.message || 'Unknown error'

    if (circuit.state === CircuitState.CLOSED) {
      // Verificar se atingiu threshold de falhas
      if (circuit.consecutiveFailures >= this.config.failureThreshold) {
        this._transitionTo(circuit, CircuitState.OPEN, endpoint)
      }
    } else if (circuit.state === CircuitState.HALF_OPEN) {
      // Falha em HALF_OPEN - voltar para OPEN
      this._transitionTo(circuit, CircuitState.OPEN, endpoint)
    }

    this._saveState()
  }

  /**
   * Registra uma recusa do conteúdo (4xx de recusa): o servidor respondeu, então não é falha
   * de disponibilidade. Não conta para abrir o circuito e fecha um circuito aberto, mas deixa
   * o rastro em lastError/lastFailureTime ('HTTP 4xx'), que o gate da saída/limpeza lê
   * (pendingGate.serverRefusedThisRound). Não mexe em lastSuccessTime: não foi envio aceito.
   *
   * @param {string} endpoint - Identificador do endpoint
   * @param {Error} error - Erro que ocorreu
   */
  recordRefusal(endpoint = 'global', error = null) {
    const circuit = this._getCircuit(endpoint)

    circuit.consecutiveFailures = 0
    circuit.lastFailureTime = Date.now()
    circuit.lastError = error?.message || 'Unknown error'

    if (circuit.state !== CircuitState.CLOSED) {
      this._transitionTo(circuit, CircuitState.CLOSED, endpoint)
    }

    this._saveState()
  }

  /**
   * Transição de estado
   */
  _transitionTo(circuit, newState, endpoint) {
    const oldState = circuit.state
    circuit.state = newState
    circuit.lastStateChange = Date.now()

    // Reset contadores ao mudar estado
    if (newState === CircuitState.CLOSED) {
      circuit.failures = 0
      circuit.successes = 0
      circuit.consecutiveFailures = 0
    } else if (newState === CircuitState.HALF_OPEN) {
      circuit.successes = 0
    }

    syncLogger.log('CIRCUIT_STATE_CHANGE', {
      endpoint,
      from: oldState,
      to: newState,
      consecutiveFailures: circuit.consecutiveFailures,
      totalFailures: circuit.totalFailures,
    })

    // Apenas transições relevantes viram evento — fluxo HALF_OPEN→CLOSED é
    // recovery automático normal e não precisa.
    if (newState === CircuitState.OPEN) {
      recordEvent({
        level: 'warn',
        category: 'sync',
        action: 'circuit_breaker_opened',
        payload: {
          endpoint,
          consecutiveFailures: circuit.consecutiveFailures,
          totalFailures: circuit.totalFailures,
          lastError: circuit.lastError,
        },
      })
    } else if (
      oldState === CircuitState.OPEN &&
      newState === CircuitState.CLOSED
    ) {
      recordEvent({
        level: 'info',
        category: 'sync',
        action: 'circuit_breaker_closed_after_recovery',
        payload: { endpoint },
      })
    }
  }

  /**
   * Executa uma função com proteção do Circuit Breaker
   *
   * @param {Function} fn - Função async a executar
   * @param {string} endpoint - Identificador do endpoint
   * @param {Function} fallback - Função fallback se circuito aberto (opcional)
   * @returns {Promise<any>} Resultado da função
   */
  async execute(fn, endpoint = 'global', fallback = null) {
    const canExec = this.canExecute(endpoint)

    if (!canExec.allowed) {
      syncLogger.log('CIRCUIT_BLOCKED', {
        endpoint,
        reason: canExec.reason,
        retryAfter: canExec.retryAfter,
      })

      if (fallback) {
        return fallback(canExec)
      }

      throw new CircuitOpenError(endpoint, canExec.retryAfter)
    }

    try {
      // Executar com timeout
      const result = await this._executeWithTimeout(
        fn,
        this.config.requestTimeout,
      )
      this.recordSuccess(endpoint)
      return result
    } catch (error) {
      this.recordFailure(endpoint, error)
      throw error
    }
  }

  /**
   * Executa função com timeout
   */
  async _executeWithTimeout(fn, timeout) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`Request timeout after ${timeout}ms`))
      }, timeout)

      fn()
        .then((result) => {
          clearTimeout(timer)
          resolve(result)
        })
        .catch((error) => {
          clearTimeout(timer)
          reject(error)
        })
    })
  }

  /**
   * Força reset de um circuito (para uso administrativo)
   *
   * @param {string} endpoint - Endpoint a resetar (ou 'all' para todos)
   */
  forceReset(endpoint = 'global') {
    if (endpoint === 'all') {
      this.globalCircuit = this._createCircuitState()
      this.circuits.clear()
      syncLogger.log('CIRCUIT_FORCE_RESET_ALL', {})
    } else if (endpoint === 'global') {
      this.globalCircuit = this._createCircuitState()
      syncLogger.log('CIRCUIT_FORCE_RESET', { endpoint: 'global' })
    } else {
      this.circuits.set(endpoint, this._createCircuitState())
      syncLogger.log('CIRCUIT_FORCE_RESET', { endpoint })
    }

    this._saveState()
  }

  /**
   * Obtém status de um circuito
   */
  getStatus(endpoint = 'global') {
    const circuit = this._getCircuit(endpoint)

    return {
      state: circuit.state,
      consecutiveFailures: circuit.consecutiveFailures,
      totalFailures: circuit.totalFailures,
      totalSuccesses: circuit.totalSuccesses,
      lastFailure: circuit.lastFailureTime
        ? new Date(circuit.lastFailureTime).toISOString()
        : null,
      lastSuccess: circuit.lastSuccessTime
        ? new Date(circuit.lastSuccessTime).toISOString()
        : null,
      lastError: circuit.lastError,
      stateAge: Date.now() - circuit.lastStateChange,
    }
  }

  /**
   * Obtém status de todos os circuitos
   */
  getAllStatus() {
    const status = {
      global: this.getStatus('global'),
      endpoints: {},
    }

    for (const [endpoint, _] of this.circuits) {
      status.endpoints[endpoint] = this.getStatus(endpoint)
    }

    return status
  }

  /**
   * Verifica se algum circuito está aberto
   */
  hasOpenCircuit() {
    if (this.globalCircuit.state === CircuitState.OPEN) {
      return true
    }

    for (const [_, circuit] of this.circuits) {
      if (circuit.state === CircuitState.OPEN) {
        return true
      }
    }

    return false
  }

  // Persistência

  _loadState() {
    try {
      const stored = localStorage.getItem('circuit_breaker_state')
      if (stored) {
        const state = JSON.parse(stored)

        if (state.globalCircuit) {
          this.globalCircuit = {
            ...this._createCircuitState(),
            ...state.globalCircuit,
          }
        }

        if (state.circuits) {
          for (const [endpoint, circuitState] of Object.entries(
            state.circuits,
          )) {
            this.circuits.set(endpoint, {
              ...this._createCircuitState(),
              ...circuitState,
            })
          }
        }
      }
    } catch (e) {
      console.error('[CircuitBreaker] failed to load state', e)
      recordError(e instanceof Error ? e : new Error(String(e)), {
        category: 'sync',
        action: 'circuit_breaker_load_state_failed',
      })
    }
  }

  _saveState() {
    try {
      const state = {
        globalCircuit: this.globalCircuit,
        circuits: Object.fromEntries(this.circuits),
        savedAt: Date.now(),
      }
      localStorage.setItem('circuit_breaker_state', JSON.stringify(state))
    } catch (e) {
      console.error('[CircuitBreaker] failed to save state', e)
      recordError(e instanceof Error ? e : new Error(String(e)), {
        category: 'sync',
        action: 'circuit_breaker_save_state_failed',
      })
    }
  }
}

/**
 * Erro lançado quando circuito está aberto
 */
export class CircuitOpenError extends Error {
  constructor(endpoint, retryAfter) {
    super(
      `Circuit breaker is open for ${endpoint}. Retry after ${retryAfter}ms`,
    )
    this.name = 'CircuitOpenError'
    this.endpoint = endpoint
    this.retryAfter = retryAfter
  }
}

// Singleton com configuração padrão
export const circuitBreaker = new CircuitBreaker()

// Factory para criar circuitos customizados
export function createCircuitBreaker(config) {
  return new CircuitBreaker(config)
}

export default CircuitBreaker
